import { ErrorBoundary } from '@sentry/react';
import { createContext, memo, useCallback, useContext, useMemo, useState } from 'react';
import { ModalErrorBoundaryFallback } from '~/components/layout/ErrorBoundaryFallback';
import { AnalyticsContext, useAnalyticsContext, AnalyticContextString } from '~/hooks/useAnalyticsContext';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { ModalTopStackItemContext } from '~/modals/ModalTopStackContext';
import { noop } from '~/utils/helpers';

export interface ModalProps {
  closeModal: (param?: PromiseResolvePayload<'CLOSE'>) => void;
}

interface PromiseResolvePayload<A extends string = string> {
  action: A;
  [key: string]: unknown;
}

interface ShowModalArgs<P extends ModalProps> {
  component: (props: P) => React.ReactElement;
  props: Omit<P, 'closeModal'>;
  context: AnalyticContextString;
}

interface ModalContextType {
  showModal<P extends ModalProps>(
    options: ShowModalArgs<P>,
  ): Promise<NonNullable<Parameters<P['closeModal']>[0]> | PromiseResolvePayload<'CLOSE'>>;
  closeModal(data?: PromiseResolvePayload): void;
  closeAllModals(data?: PromiseResolvePayload): void;
}

let modalId = 1;

const ModalContext = createContext<ModalContextType>({
  showModal: () => Promise.resolve({ action: 'CLOSE' }),
  closeModal: noop,
  closeAllModals: noop,
});

export const useModal = () => {
  const { showModal, closeModal, closeAllModals } = useContext(ModalContext);
  const context = useAnalyticsContext();

  return {
    closeModal,
    closeAllModals,
    showModal: <P extends ModalProps>(options: Omit<ShowModalArgs<P>, 'context'>) => showModal({ ...options, context }),
  };
};

interface ModalProviderProps {
  children: React.ReactNode;
}

const Modal = ({ modal, closeModal }: { modal: ModalData; closeModal(data?: PromiseResolvePayload): void }) => {
  const ModalComponent = modal.component;
  const { decorateScopeContexts } = useExceptionLogger();

  return (
    <ErrorBoundary
      beforeCapture={(scope, error) => {
        decorateScopeContexts(scope, error);
      }}
      fallback={(props) => <ModalErrorBoundaryFallback {...props} onClose={closeModal} />}
    >
      <AnalyticsContext context={modal.context}>
        <ModalComponent {...modal.props} closeModal={closeModal} />
      </AnalyticsContext>
    </ErrorBoundary>
  );
};

const MemoModal = memo(Modal, ({ modal: m1 }, { modal: m2 }) => m1.id === m2.id);

interface ModalData {
  id: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: (props: any) => React.ReactElement;
  props: Record<string, unknown>;
  context: AnalyticContextString;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolve: (data: PromiseResolvePayload<any>) => void;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modals, setModals] = useState<ModalData[]>([]);

  const closeModal = useCallback<ModalContextType['closeModal']>((data = { action: 'CLOSE' }) => {
    setModals((prevState) => {
      prevState[prevState.length - 1]?.resolve(data);
      return prevState.slice(0, -1);
    });
  }, []);

  const closeAllModals = useCallback<ModalContextType['closeAllModals']>((data = { action: 'CLOSE' }) => {
    setModals((prevState) => {
      prevState.forEach((modal) => modal.resolve(data));
      return [];
    });
  }, []);

  const showModal = useCallback<ModalContextType['showModal']>(({ component, props, context }) => {
    return new Promise((resolve) => {
      setModals((prevState) => [...prevState, { component, props, resolve, context, id: modalId++ }]);
    });
  }, []);

  const value = useMemo(() => {
    return { showModal, closeModal, closeAllModals };
  }, [showModal, closeModal, closeAllModals]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      {modals.map((modal, index) => (
        <ModalTopStackItemContext.Provider key={modal.id} value={index === modals.length - 1}>
          <MemoModal modal={modal} closeModal={closeModal} />
        </ModalTopStackItemContext.Provider>
      ))}
    </ModalContext.Provider>
  );
};
